import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { PanInfo } from 'framer-motion';
import { cn } from '@/lib/utils';

interface Tab {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface SwipeableTabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  children: React.ReactNode[];
  className?: string;
}

export const SwipeableTabs: React.FC<SwipeableTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  children,
  className,
}) => {
  const [direction, setDirection] = useState<number>(0);
  const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);

  const handleSwipe = (offset: number, velocity: number) => {
    const swipeThreshold = 50;
    const swipeVelocityThreshold = 500;

    if (Math.abs(offset) > swipeThreshold || Math.abs(velocity) > swipeVelocityThreshold) {
      if (offset > 0 && activeIndex > 0) {
        // Swipe right - go to previous tab
        setDirection(-1);
        onTabChange(tabs[activeIndex - 1].id);
      } else if (offset < 0 && activeIndex < tabs.length - 1) {
        // Swipe left - go to next tab
        setDirection(1);
        onTabChange(tabs[activeIndex + 1].id);
      }
    }
  };

  const handleTabClick = (tabId: string) => {
    const newIndex = tabs.findIndex((tab) => tab.id === tabId);
    setDirection(newIndex > activeIndex ? 1 : -1);
    onTabChange(tabId);
  };

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Tab Navigation */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Tabs */}
          <div className="hidden lg:flex items-center gap-1 py-2 overflow-x-auto">
            {tabs.map((tab) => {
              const isActive = tab.id === activeTab;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                    isActive
                      ? 'bg-collectability-blue text-white '
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  )}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Mobile Tabs */}
          <div className="lg:hidden flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const isActive = tab.id === activeTab;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap flex-shrink-0',
                    isActive
                      ? 'bg-collectability-blue text-white '
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  )}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content with Swipe Support */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={activeTab}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(e, { offset, velocity }: PanInfo) => {
              handleSwipe(offset.x, velocity.x);
            }}
            className="w-full min-h-full"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              {children[activeIndex]}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Mobile Page Indicators */}
      <div className="lg:hidden sticky bottom-0 bg-white border-t border-slate-200 py-3">
        <div className="flex justify-center gap-2">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={cn(
                'w-2 h-2 rounded-full transition-all',
                index === activeIndex
                  ? 'bg-collectability-blue w-8'
                  : 'bg-slate-300 hover:bg-slate-400'
              )}
              aria-label={`Go to ${tab.label}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

